/**
 * Paragraph splitting for compendium prose.
 *
 * Compendium bodies (`@massif/lancer-data` and the packs built on its shape) mark a paragraph
 * boundary with a `<br>`, not a `<p>` — a single `<br>` in frames/weapons/systems/talents, a
 * `<br><br>` in manufacturers/glossary. Dumped into one element that reads as an undifferentiated
 * wall: a `<br>` renders exactly like a wrapped line, so an eleven-paragraph frame description has
 * no visible paragraph boundaries at all, and a one-line paragraph is indistinguishable from a wrap.
 *
 * The fix has to happen here rather than in CSS: Blink generates no box for a `<br>`, so `display`,
 * `height`, `margin` and even `::after` on it are all silently inert. Splitting the string into real
 * per-paragraph elements is what lets the stylesheet put a gap between them.
 */

/**
 * A run of one or more `<br>` (plus surrounding whitespace) — the paragraph delimiter. Matching the
 * whole run, not one tag, is what makes `<br>` and `<br><br>` sources produce the same single gap.
 */
const BR_RUN = /(?:\s*<br\s*\/?>\s*)+/gi;

/**
 * Split compendium HTML into its paragraphs, trimmed, with empties dropped. A body with no `<br>`
 * comes back as a single entry — callers use that to render it exactly as before, untouched.
 */
export function splitProseParagraphs(html: string): string[] {
  return html
    .split(BR_RUN)
    .map((chunk) => chunk.trim())
    .filter((chunk) => chunk.length > 0);
}
